import { useCallback, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { adminOrderService } from '../services'
import {
  ARTICLE_CODE_HEADER,
  QTY_HEADER,
  parseAndValidateManualWebhookExcel,
} from '../utils/manualWebhookExcelImport'

export const MANUAL_WEBHOOK_CONFIRM_PHRASE = 'confirm manual webhook'

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = bytes.map((value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function linesFromShipment(shipment) {
  const bySku = new Map()
  for (const item of shipment?.items || []) {
    const material = String(item.sku || '').trim()
    if (!material) return { error: 'An article on this shipment has no SKU.', lines: [] }
    const quantity = Number(item.quantity)
    if (!Number.isFinite(quantity) || quantity < 0) {
      return { error: 'Current assigned quantity is invalid.', lines: [] }
    }
    if (bySku.has(material)) {
      return { error: 'This shipment has the same SKU on more than one line.', lines: [] }
    }
    bySku.set(material, {
      material,
      name: item.product_name || material,
      quantity,
      finalQuantity: String(quantity),
    })
  }
  if (bySku.size === 0) return { error: 'This shipment has no articles.', lines: [] }
  return { error: null, lines: [...bySku.values()] }
}

export default function ManualWebhookDialog({
  orderId,
  shipment,
  canSubmit,
  onRefresh,
}) {
  const prepared = useMemo(() => linesFromShipment(shipment), [shipment])
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState('edit')
  const [lines, setLines] = useState([])
  const [phrase, setPhrase] = useState('')
  const [error, setError] = useState(null)
  const [excelErrors, setExcelErrors] = useState([])
  const [excelWarnings, setExcelWarnings] = useState([])
  const [excelFileName, setExcelFileName] = useState('')
  const [parsingExcel, setParsingExcel] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const fileInputRef = useRef(null)
  const [idempotencyKey, setIdempotencyKey] = useState('')

  const resetExcelState = useCallback(() => {
    setExcelErrors([])
    setExcelWarnings([])
    setExcelFileName('')
    setParsingExcel(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const openDialog = () => {
    setLines(prepared.lines.map((line) => ({ ...line })))
    setStep('edit')
    setPhrase('')
    setError(prepared.error)
    setIdempotencyKey(newIdempotencyKey())
    resetExcelState()
    setOpen(true)
  }

  const parsedLines = lines.map((line) => {
    const finalQuantity = Number(line.finalQuantity)
    const invalid =
      line.finalQuantity === '' ||
      !Number.isFinite(finalQuantity) ||
      finalQuantity < 0 ||
      finalQuantity > line.quantity
    return { ...line, finalQuantityNumber: finalQuantity, invalid }
  })
  const quantitiesValid = parsedLines.length > 0 && parsedLines.every((line) => !line.invalid)
  const phraseMatches = phrase === MANUAL_WEBHOOK_CONFIRM_PHRASE
  const busy = submitting || parsingExcel

  const handleExcelSelected = useCallback(
    async (event) => {
      const file = event.target.files?.[0]
      if (!file || busy) return

      setParsingExcel(true)
      setExcelErrors([])
      setExcelWarnings([])
      setError(null)
      setExcelFileName(file.name)

      try {
        const buffer = await file.arrayBuffer()
        const result = parseAndValidateManualWebhookExcel(buffer, {
          lines: prepared.lines,
        })

        if (!result.ok) {
          setExcelErrors(result.errors || [])
          setExcelWarnings(result.warnings || [])
          return
        }

        setExcelErrors([])
        setExcelWarnings(result.warnings || [])
        setLines((current) => {
          const byMaterial = new Map(
            (result.updates || []).map((row) => [row.material, row.finalQuantity]),
          )
          return current.map((line) => {
            if (!byMaterial.has(line.material)) return line
            return {
              ...line,
              finalQuantity: String(byMaterial.get(line.material)),
            }
          })
        })
      } catch (err) {
        setExcelErrors([
          {
            message:
              err?.message ||
              `Could not read the Excel file. Use .xlsx or .xls with ${ARTICLE_CODE_HEADER} and ${QTY_HEADER} columns.`,
          },
        ])
      } finally {
        setParsingExcel(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [busy, prepared.lines],
  )

  const submit = async () => {
    if (submittingRef.current || !phraseMatches || !quantitiesValid || busy) return
    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    try {
      await adminOrderService.submitManualWebhook(orderId, shipment.id, {
        idempotency_key: idempotencyKey,
        items: parsedLines.map((line) => ({
          material: line.material,
          final_quantity: line.finalQuantityNumber,
        })),
      })
      setOpen(false)
      onRefresh?.()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not submit the webhook.')
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  if (!canSubmit) return null

  return (
    <>
      <Button size="small" variant="outlined" onClick={openDialog}>
        Manual webhook
      </Button>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Manual webhook</DialogTitle>
        <DialogContent>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {Array.isArray(error) ? error.join(' ') : error}
            </Alert>
          ) : null}
          {step === 'edit' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Current assigned quantity is taken from the shipment. Enter the shipped quantity
                for the webhook. Zero ships nothing.
              </Typography>

              <Box
                sx={{
                  p: 2,
                  border: '1px solid #eee',
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Assign from Excel
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Upload <strong>{ARTICLE_CODE_HEADER}</strong> and <strong>{QTY_HEADER}</strong>.
                  Qty is the shipped quantity for that article (0 up to current assigned). Articles
                  omitted from the file keep their current assigned values below. You can still edit
                  quantities manually after upload.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    hidden
                    onChange={handleExcelSelected}
                    disabled={busy || Boolean(prepared.error)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={
                      parsingExcel ? <CircularProgress size={16} /> : <CloudUploadIcon />
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy || Boolean(prepared.error)}
                  >
                    {parsingExcel ? 'Reading Excel…' : 'Upload Excel'}
                  </Button>
                  {excelFileName ? (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {excelFileName}
                    </Typography>
                  ) : null}
                  {(excelFileName || excelErrors.length > 0) && (
                    <Button size="small" onClick={resetExcelState} disabled={busy}>
                      Clear
                    </Button>
                  )}
                </Stack>
                {excelErrors.length > 0 && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Fix these issues before uploading again
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {excelErrors.map((err, idx) => (
                        <li key={`${err.excelRow || 'e'}-${idx}`}>
                          <Typography variant="body2">{err.message}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
                {excelWarnings.length > 0 && excelErrors.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {excelWarnings.map((warning, idx) => (
                        <li key={`${warning.articleCode || 'w'}-${idx}`}>
                          <Typography variant="body2">{warning.message}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
              </Box>

              {parsedLines.map((line) => (
                <Box key={line.material}>
                  <Typography variant="body2">
                    {line.name} ({line.material}) — Current Assigned: {line.quantity}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="Shipped Quantity"
                    value={line.finalQuantity}
                    inputProps={{ min: 0, max: line.quantity, step: '0.01' }}
                    error={line.invalid}
                    helperText={
                      line.invalid
                        ? 'Enter a shipped quantity from 0 up to the current assigned quantity'
                        : undefined
                    }
                    disabled={busy}
                    onChange={(event) =>
                      setLines((current) =>
                        current.map((row) =>
                          row.material === line.material
                            ? { ...row, finalQuantity: event.target.value }
                            : row,
                        ),
                      )
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Review shipped quantities (current assigned → shipped quantity).
              </Typography>
              {parsedLines.map((line) => (
                <Typography key={line.material} variant="body2">
                  {line.material}: Current Assigned {line.quantity} → Shipped Quantity{' '}
                  {line.finalQuantityNumber}
                </Typography>
              ))}
              <TextField
                size="small"
                label={`Type ${MANUAL_WEBHOOK_CONFIRM_PHRASE}`}
                value={phrase}
                onChange={(event) => setPhrase(event.target.value)}
                autoComplete="off"
                disabled={busy}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          {step === 'edit' ? (
            <Button
              variant="contained"
              disabled={!quantitiesValid || Boolean(prepared.error) || busy}
              onClick={() => setStep('confirm')}
            >
              Review
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!phraseMatches || busy}
              onClick={submit}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
